<template>
    <div class="product-page angler-page">

        <section class="product-hero">
            <div class="product-content">

                <div class="hero-grid">

                    <div class="hero-text">
                        <h1>Angler™</h1>

                        <h2>Your Window Into the Underwater World</h2>

                        <p>
                            The Angler™ Self-Cleaning Underwater Camera System is a professional-grade,
                            fixed-view underwater observatory designed for continuous monitoring in marine environments.
                        </p>

                        <p>
                            Engineered for long-term deployment, Angler combines high-definition video,
                            self-cleaning optics, remote connectivity, and low-power operation to provide
                            a reliable view beneath the surface.
                        </p>

                        <b-button @click="$router.push('/contact')">
                            Schedule a Consultation
                        </b-button>
                    </div>

                    <div class="hero-image">
                        <img
                            src="@/assets/images/angler-product.png"
                            alt="Angler"
                        >
                    </div>

                </div>

            </div>
        </section>

        <section class="product-section product-benefits">
            <div class="product-content">

                <div class="pink-line"></div>

                <h2>Key Benefits</h2>

                <div class="benefit-grid">

                    <div class="benefit-card">
                        <h3>Self-Cleaning Technology</h3>
                        <p>
                            Mechanical brush cleaning eliminates biofouling and lens buildup.
                        </p>
                    </div>

                    <div class="benefit-card">
                        <h3>Continuous HD Monitoring</h3>
                        <p>
                            4MP high-definition video and time-lapse imagery.
                        </p>
                    </div>

                    <div class="benefit-card">
                        <h3>Remote Access Anywhere</h3>
                        <p>
                            Monitor live video remotely through Ethernet, Wi-Fi, or Starlink.
                        </p>
                    </div>

                    <div class="benefit-card">
                        <h3>Built for Long-Term Deployment</h3>
                        <p>
                            Marine-grade construction and low-power operation.
                        </p>
                    </div>

                    <div class="benefit-card">
                        <h3>Expandable & Scalable</h3>
                        <p>
                            Support multi-camera deployments and larger monitoring networks.
                        </p>
                    </div>

                </div>

            </div>
        </section>

        <section class="angler-live-camera" aria-labelledby="florida-keys-doc-cam-title">
            <div class="angler-live-camera__content">
                <div class="angler-live-camera__introduction">
                    <h2 id="florida-keys-doc-cam-title">Florida Keys Dock Cam</h2>
                    <p>
                        A live Angler camera in the Florida Keys demonstrates the system in a residential installation.
                    </p>
                </div>

                <div class="angler-live-camera__frame">
                    <iframe
                        src="https://www.youtube-nocookie.com/embed/cLG_Y-rPPTw"
                        title="Florida Keys Dock Cam live underwater camera"
                        loading="lazy"
                        referrerpolicy="strict-origin-when-cross-origin"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowfullscreen
                    ></iframe>
                </div>
            </div>
        </section>

        <section class="product-section product-specifications" aria-labelledby="angler-specifications-title">
            <div class="product-content product-specifications__layout">
                <div class="product-specifications__heading">
                    <h2 id="angler-specifications-title">Technical Specifications</h2>
                    <p>Self-Cleaning Underwater Camera System</p>
                </div>

                <div class="technical-ledger">
                    <div><strong>Camera Type</strong><span>Fixed-view underwater camera</span></div>
                    <div><strong>Video</strong><span>2688 × 1512, 4MP HD, 30 FPS</span></div>
                    <div><strong>Lens</strong><span>2.4 mm, F2.1 wide-angle lens</span></div>
                    <div><strong>Field of View</strong><span>130° horizontal, 93° vertical</span></div>
                    <div><strong>Streaming</strong><span>RTSP / H.264</span></div>
                    <div><strong>Self-Cleaning</strong><span>Replaceable mechanical brush</span></div>
                    <div><strong>Power</strong><span>90–250 VAC or 12–48 VDC</span></div>
                    <div><strong>Power Consumption</strong><span>5W camera</span></div>
                    <div><strong>Housing</strong><span>Acetal and glass</span></div>
                    <div><strong>Depth Rating</strong><span>50 meters</span></div>
                    <div><strong>Cable Length</strong><span>&lt;200m standard, &gt;200m Heliax</span></div>
                    <div><strong>Mounting</strong><span>Standard or custom mounting available</span></div>
                </div>
            </div>
        </section>

        <section class="product-section operational-profile">
            <div class="product-content operational-profile__layout">
                <h2>Ideal For</h2>

                <div class="ideal-grid">
                    <div class="card">Waterfront Homes</div>
                    <div class="card">Marinas & Yacht Clubs</div>
                    <div class="card">Educational Institutions</div>
                    <div class="card">Conservation Organizations</div>
                    <div class="card">Environmental Monitoring</div>
                    <div class="card">Research Projects</div>
                    <div class="card">Ports & Waterfront Facilities</div>
                </div>
            </div>
        </section>

        <section class="cta-section product-consult">
            <div class="cta-content">

                <h2>Ready to See What's Below?</h2>

                <p>
                    Discover how Angler™ can provide a continuous window into your underwater environment.
                </p>

                <b-button @click="$router.push('/contact')">
                    Schedule a Consultation
                </b-button>

            </div>
        </section>

    </div>
</template>

<script>
export default {
    name: 'Angler'
}
</script>

<style>
#app .angler-live-camera {
    padding: clamp(88px, 9vw, 142px) clamp(24px, 5vw, 72px);
    color: #071323;
    background: #f7f2f5;
}

#app .angler-live-camera__content {
    display: grid;
    width: min(100%, 1540px);
    margin: 0 auto;
    grid-template-columns: minmax(250px, 0.55fr) minmax(0, 1.45fr);
    gap: clamp(48px, 7vw, 118px);
    align-items: end;
}

#app .angler-live-camera__introduction {
    padding-bottom: clamp(10px, 2vw, 34px);
}

#app .angler-live-camera__introduction h2 {
    max-width: 9ch;
    margin: 0 0 30px;
    color: #071323;
    font-size: clamp(3.3rem, 5.2vw, 6.4rem);
    font-weight: 500;
    line-height: 0.93;
    letter-spacing: -0.055em;
}

#app .angler-live-camera__introduction p {
    max-width: 39ch;
    margin: 0;
    color: #344451;
    font-size: clamp(1.05rem, 1.2vw, 1.2rem);
    line-height: 1.7;
}

#app .angler-live-camera__frame {
    position: relative;
    overflow: hidden;
    background: #071323;
    aspect-ratio: 16 / 9;
}

#app .angler-live-camera__frame::before {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    z-index: 1;
    height: 1px;
    content: "";
    background: rgba(255, 255, 255, 0.28);
    pointer-events: none;
}

#app .angler-live-camera__frame iframe {
    display: block;
    width: 100%;
    height: 100%;
    border: 0;
}

@media (max-width: 900px) {
    #app .angler-live-camera__content {
        grid-template-columns: 1fr;
        gap: 42px;
    }

    #app .angler-live-camera__introduction h2 {
        max-width: none;
    }
}

@media (max-width: 520px) {
    #app .angler-live-camera {
        padding: 76px 20px;
    }

    #app .angler-live-camera__introduction h2 {
        margin-bottom: 22px;
        font-size: clamp(2.8rem, 14vw, 4rem);
    }
}
</style>
